import React, { ReactNode } from 'react';
import { Drawer, Button, DrawerProps } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import useSwipeDown from 'lib.hooks/src/useSwipeDown';
import { useBreakpoints } from 'lib.hooks/src';
import './styles.scss';

type DrawerCustomProps = {
  title?: ReactNode;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Used on mobile for the primary (Apply) button */
  onOk?: () => void;
  /** Used on mobile for the secondary (Cancel/Reset) button. Defaults to onClose when not provided. */
  onCancel?: () => void;
  applyButtonText?: string;
  resetButtonText?: string;
  isLoading?: boolean;
  isShowResetBtn?: boolean;
  propClassNames?: DrawerProps['classNames'];
  /** When true, always use mobile (bottom sheet) layout regardless of breakpoint. */
  forceMobile?: boolean;
  applyDisabled?: boolean;
} & Omit<DrawerProps, 'title' | 'open' | 'onClose' | 'children'>;

const DrawerCustom: React.FC<DrawerCustomProps> = (props) => {
  const {
    title,
    open,
    onClose,
    children,
    onOk,
    onCancel,
    applyButtonText = 'Ok',
    resetButtonText = 'Cancel',
    isLoading,
    isShowResetBtn = true,
    propClassNames,
    extra,
    forceMobile,
    footer,
    applyDisabled,
    ...restProps
  } = props;

  const breakpointMd = useBreakpoints().md;
  const isDesktop = breakpointMd && !forceMobile;
  const { onTouchStart, onTouchEnd } = useSwipeDown(onClose);

  if (isDesktop) {
    return (
      <Drawer
        {...restProps}
        title={title}
        open={open}
        onClose={onClose}
        footer={footer}
        rootStyle={restProps.rootStyle ?? { top: 'var(--shell-top, 62px)' }}
        width={450}
        closeIcon={props.closeIcon ?? false}
        placement={props.placement ?? 'right'}
        autoFocus={props.autoFocus ?? false}
        extra={extra !== undefined ? extra : <CloseOutlined onClick={onClose} />}
        rootClassName="styled-drawer"
      >
        {children}
      </Drawer>
    );
  }

  const handleCancel = onCancel ?? onClose;
  const handleOk = onOk ?? onClose;

  const mobileClassNames = {
    ...(propClassNames ?? {}),
    ...(restProps.classNames ?? {}),
  };

  const mobileFooter =
    footer === undefined ? (
      <footer className="mobile-drawer__footer">
        {isShowResetBtn ? (
          <Button size="large" block onClick={handleCancel}>
            {resetButtonText}
          </Button>
        ) : null}
        <Button
          block
          type="primary"
          size="large"
          onClick={handleOk}
          loading={isLoading}
          disabled={applyDisabled}
        >
          {applyButtonText}
        </Button>
      </footer>
    ) : footer === null ? undefined : (
      <div className="mobile-drawer__footer">{footer}</div>
    );

  return (
    <Drawer
      {...restProps}
      closeIcon={null}
      placement="bottom"
      title={
        <div onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} className="mobile-drawer__header">
          <svg
            className="mobile-drawer__header-handle"
            width="51"
            height="4"
            viewBox="0 0 51 4"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M1.5 2H49.5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          </svg>
          <div className="mobile-drawer__header-title">
            <div />
            <span className="mobile-drawer__header-title-text">{title ?? ''}</span>
            <div className="flex-center mobile-drawer__header-close" onClick={onClose}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M16.5893 4.59056C16.9148 4.26512 16.9148 3.73748 16.5893 3.41205C16.2639 3.08661 15.7363 3.08661 15.4108 3.41205L10.0001 8.82279L4.58934 3.41205C4.2639 3.08661 3.73626 3.08661 3.41083 3.41205C3.08539 3.73748 3.08539 4.26512 3.41083 4.59056L8.82157 10.0013L3.41083 15.412C3.08539 15.7375 3.08539 16.2651 3.41083 16.5906C3.73626 16.916 4.2639 16.916 4.58934 16.5906L10.0001 11.1798L15.4108 16.5906C15.7363 16.916 16.2639 16.916 16.5893 16.5906C16.9148 16.2651 16.9148 15.7375 16.5893 15.412L11.1786 10.0013L16.5893 4.59056Z"
                  fill="currentColor"
                />
              </svg>
            </div>
          </div>
        </div>
      }
      onClose={onClose}
      open={open}
      className="mobile-drawer"
      rootClassName="mobile-drawer-root"
      footer={mobileFooter}
      classNames={mobileClassNames}
    >
      <div
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        className="flex flex-col drawer-filter-content"
      >
        {children}
      </div>
    </Drawer>
  );
};

export default DrawerCustom;
